/**
 * Drag Scroll Utility
 * 横スクロール可能な要素にdata属性[data-drag-scroll]を付与して自動適用
 * MutationObserverでDOM置換後の要素にも自動で再適用
 */

import { breakpoint } from "../constant/breakpoint";

// 修正: ドラッグ判定の移動閾値（px）。この値未満の移動はクリックとして扱う
const DRAG_THRESHOLD = 5;

// 修正: DOM属性ではなくJSプロパティで初期化済みフラグを管理（MutationObserverの再トリガー防止）
const INIT_KEY = "__dragScrollInit__";

interface DragScrollElement extends HTMLElement {
  [INIT_KEY]?: boolean;
}

const setupDragScroll = (element: DragScrollElement): void => {
  if (element[INIT_KEY]) return;
  element[INIT_KEY] = true;

  let isDown = false;
  let startX: number;
  let scrollLeft: number;
  let hasMoved = false;

  // リンクや画像のデフォルトのドラッグ動作を防止
  const links = element.querySelectorAll<HTMLAnchorElement>("a");
  const images = element.querySelectorAll<HTMLImageElement>("img");

  for (const link of Array.from(links)) {
    link.setAttribute("draggable", "false");
  }

  for (const image of Array.from(images)) {
    image.setAttribute("draggable", "false");
  }

  element.addEventListener("dragstart", (e: DragEvent) => {
    e.preventDefault();
  });

  element.addEventListener("mousedown", (e: MouseEvent) => {
    isDown = true;
    hasMoved = false;
    element.style.userSelect = "none";
    startX = e.pageX - element.offsetLeft;
    scrollLeft = element.scrollLeft;
  });

  element.addEventListener("mouseleave", () => {
    isDown = false;
  });

  element.addEventListener("mouseup", () => {
    isDown = false;
  });

  element.addEventListener("mousemove", (e: MouseEvent) => {
    if (!isDown) return;
    const x = e.pageX - element.offsetLeft;
    const distance = Math.abs(x - startX);
    // 修正: 閾値以上の移動があった場合のみドラッグとして扱う
    if (distance >= DRAG_THRESHOLD) {
      e.preventDefault();
      hasMoved = true;
    }
    const walk = (x - startX) * 2;
    element.scrollLeft = scrollLeft - walk;
  });

  element.addEventListener(
    "click",
    (e: Event) => {
      if (hasMoved) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    true,
  );
};

export const initDragScroll = (): void => {
  const mediaQuery = window.matchMedia(`(min-width: ${breakpoint.md})`);

  const applyDragScroll = (): void => {
    if (!mediaQuery.matches) return;

    const elements =
      document.querySelectorAll<DragScrollElement>("[data-drag-scroll]");
    for (const element of Array.from(elements)) {
      setupDragScroll(element);
    }
  };

  applyDragScroll();

  // 修正: DOM置換（Shopify viewTransition等）後の新要素にも再適用（debounceで無限ループ防止）
  let timer: ReturnType<typeof setTimeout> | null = null;
  const observer = new MutationObserver(() => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(applyDragScroll, 200);
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
};
